import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, X, Check, ChevronRight, Calendar, CreditCard, ExternalLink, UserPlus } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { api, type Notification } from '../lib/api'
import { useAuthStore } from '../lib/auth'

// Global event bus for SSE updates across components
type SSEEventHandler = (data: Record<string, unknown>) => void
const sseEventHandlers = new Map<string, Set<SSEEventHandler>>()

export function subscribeToSSE(eventType: string, handler: SSEEventHandler) {
  if (!sseEventHandlers.has(eventType)) {
    sseEventHandlers.set(eventType, new Set())
  }
  sseEventHandlers.get(eventType)!.add(handler)
  return () => {
    sseEventHandlers.get(eventType)?.delete(handler)
  }
}

function emitSSEEvent(eventType: string, data: Record<string, unknown>) {
  sseEventHandlers.get(eventType)?.forEach(handler => handler(data))
}

// Shared SSE connection management
let sseConnection: EventSource | null = null
let sseConnectionPromise: Promise<EventSource> | null = null

function getSSEConnection(): Promise<EventSource> {
  if (sseConnection && sseConnection.readyState !== EventSource.CLOSED) {
    return Promise.resolve(sseConnection)
  }
  
  if (sseConnectionPromise) {
    return sseConnectionPromise
  }
  
  sseConnectionPromise = new Promise((resolve, reject) => {
    const baseUrl = import.meta.env.VITE_API_URL || ''
    const url = `${baseUrl}/api/v1/notifications/stream`
    
    // Use withCredentials to send cookies for authentication
    const eventSource = new EventSource(url, { withCredentials: true })
    
    eventSource.onopen = () => {
      console.log('[SSE] Connected to notification stream')
      sseConnection = eventSource
      sseConnectionPromise = null
      resolve(eventSource)
    }
    
    eventSource.onerror = (error) => {
      console.warn('[SSE] Connection error, will retry:', error)
      sseConnectionPromise = null
      if (eventSource.readyState === EventSource.CLOSED) {
        sseConnection = null
      }
      reject(error)
    }
    
    // Listen for notification events
    eventSource.addEventListener('notification', (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('[SSE] Notification event:', data)
        emitSSEEvent('notification', data)
      } catch (e) {
        console.error('[SSE] Failed to parse notification event:', e)
      }
    })
    
    // Listen for booking update events
    eventSource.addEventListener('booking_update', (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('[SSE] Booking update event:', data)
        emitSSEEvent('booking_update', data)
      } catch (e) {
        console.error('[SSE] Failed to parse booking_update event:', e)
      }
    })
    
    // Listen for access request events
    eventSource.addEventListener('access_request', (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('[SSE] Access request event:', data)
        emitSSEEvent('access_request', data)
      } catch (e) {
        console.error('[SSE] Failed to parse access_request event:', e)
      }
    })
    
    // Listen for notifications_updated events (when notifications are marked read elsewhere)
    eventSource.addEventListener('notifications_updated', (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('[SSE] Notifications updated event:', data)
        emitSSEEvent('notifications_updated', data)
      } catch (e) {
        console.error('[SSE] Failed to parse notifications_updated event:', e)
      }
    })
    
    // Heartbeat just keeps connection alive, no action needed
    eventSource.addEventListener('heartbeat', () => {
      // Connection is alive
    })
  })
  
  return sseConnectionPromise
}

export function NotificationBell() {
  const { checkAuth, setCurrentOrg, memberships } = useAuthStore()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Load unread count function
  const loadUnreadCount = useCallback(async () => {
    try {
      const { count } = await api.notifications.getUnreadCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Failed to load unread count:', error)
    }
  }, [])

  // Set up SSE connection for real-time updates
  useEffect(() => {
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    let isActive = true
    
    async function connectSSE() {
      if (!isActive) return
      
      try {
        await getSSEConnection()
      } catch {
        // Retry after 5 seconds on connection failure
        if (isActive) {
          retryTimeout = setTimeout(connectSSE, 5000)
        }
      }
    }
    
    connectSSE()
    
    // Subscribe to notification events
    const unsubNotification = subscribeToSSE('notification', () => {
      // Reload unread count when we receive a notification
      loadUnreadCount()
      // If dropdown is open, reload full list
      if (isOpen) {
        loadNotifications()
      }
    })
    
    // Subscribe to notifications_updated events (when notifications marked read from elsewhere)
    const unsubUpdated = subscribeToSSE('notifications_updated', () => {
      // Reload unread count when notifications are marked read
      loadUnreadCount()
      // If dropdown is open, reload full list
      if (isOpen) {
        loadNotifications()
      }
    })
    
    return () => {
      isActive = false
      if (retryTimeout) clearTimeout(retryTimeout)
      unsubNotification()
      unsubUpdated()
    }
  }, [loadUnreadCount, isOpen])

  // Load unread count on mount and as fallback polling (less frequent now)
  useEffect(() => {
    loadUnreadCount()
    // Fallback polling every 5 minutes (instead of 1 minute) since we have SSE
    const interval = setInterval(loadUnreadCount, 300000)
    return () => clearInterval(interval)
  }, [loadUnreadCount])

  // Load full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications()
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function loadNotifications() {
    setLoading(true)
    try {
      const { notifications: data } = await api.notifications.list()
      setNotifications(data)
      // Update unread count based on loaded notifications
      setUnreadCount(data.filter(n => !n.readAt).length)
    } catch (error) {
      console.error('Failed to load notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleMarkAsRead(notificationId: string) {
    try {
      await api.notifications.markAsRead(notificationId)
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
      ))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  async function handleMarkAllAsRead() {
    try {
      await api.notifications.markAllAsRead()
      setNotifications(prev => prev.map(n => ({ ...n, readAt: new Date().toISOString() })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  async function handleAcceptInvite(notification: Notification) {
    setActionLoading(notification.id)
    try {
      const result = await api.notifications.acceptInvite(notification.id)
      if (result.success) {
        // Update the notification as actioned
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, actionedAt: new Date().toISOString(), readAt: new Date().toISOString() } 
            : n
        ))
        setUnreadCount(prev => Math.max(0, prev - 1))
        
        // Refresh auth to get new membership
        await checkAuth()
        
        // Show success message (could use a toast)
        alert(`You've joined ${result.orgName || 'the organisation'}!`)
        
        // Optionally reload the page to reflect new org
        window.location.reload()
      }
    } catch (error) {
      console.error('Failed to accept invite:', error)
      alert('Failed to accept invite. Please try again.')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDeclineInvite(notification: Notification) {
    setActionLoading(notification.id)
    try {
      await api.notifications.declineInvite(notification.id)
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, actionedAt: new Date().toISOString(), readAt: new Date().toISOString() } 
          : n
      ))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Failed to decline invite:', error)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete(notificationId: string) {
    try {
      await api.notifications.delete(notificationId)
      const wasUnread = notifications.find(n => n.id === notificationId && !n.readAt)
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Failed to delete notification:', error)
    }
  }

  function formatTime(dateStr: string) {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-16 sm:top-full sm:mt-2 sm:w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    actionLoading={actionLoading === notification.id}
                    onMarkAsRead={() => handleMarkAsRead(notification.id)}
                    onAccept={() => handleAcceptInvite(notification)}
                    onDecline={() => handleDeclineInvite(notification)}
                    onDelete={() => handleDelete(notification.id)}
                    onViewBooking={async () => {
                      const orgSlug = notification.data?.orgSlug as string | undefined
                      const bookingId = notification.data?.bookingId as string | undefined
                      setIsOpen(false)

                      // Managers of that org review it in the manage Bookings list;
                      // everyone else views it on their personal My Bookings page
                      // (which needs no org switch - it spans all orgs).
                      const managesOrg = memberships.some(
                        (m) =>
                          m.orgSlug === orgSlug && (m.role === 'admin' || m.role === 'approver')
                      )
                      if (managesOrg && orgSlug) {
                        await setCurrentOrg(orgSlug)
                        navigate({
                          to: '/dashboard/bookings',
                          search: bookingId ? { open: bookingId } : undefined,
                        })
                      } else {
                        navigate({
                          to: '/my-bookings',
                          search: bookingId ? { open: bookingId } : undefined,
                        })
                      }
                    }}
                    onViewRequest={async () => {
                      // Navigate to requests page to view access request
                      setIsOpen(false)
                      navigate({ to: '/dashboard/requests' })
                    }}
                    formatTime={formatTime}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function NotificationItem({
  notification,
  actionLoading,
  onMarkAsRead,
  onAccept,
  onDecline,
  onDelete,
  onViewBooking,
  onViewRequest,
  formatTime,
}: {
  notification: Notification
  actionLoading: boolean
  onMarkAsRead: () => void
  onAccept: () => void
  onDecline: () => void
  onDelete: () => void
  onViewBooking: () => void
  onViewRequest: () => void
  formatTime: (dateStr: string) => string
}) {
  const isUnread = !notification.readAt
  const isInviteActionable = notification.type === 'org_invite' && !notification.actionedAt
  const isBookingNotification = notification.type === 'booking_approved' || notification.type === 'booking_rejected' || notification.type === 'payment_confirmed'
  const isAccessRequest = notification.type === 'access_request'
  // Check if this is a booking request notification (system type with bookingId)
  const isBookingRequest = notification.type === 'system' && notification.data?.bookingId
  const requiresPayment = notification.type === 'booking_approved' && notification.data?.requiresPayment
  const isClickable = isBookingNotification || isAccessRequest || isBookingRequest

  return (
    <div 
      className={`p-4 hover:bg-gray-50 transition-colors ${isUnread ? 'bg-blue-50/50' : ''} ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={() => {
        if (isBookingNotification || isBookingRequest) {
          onViewBooking()
        } else if (isAccessRequest) {
          onViewRequest()
        } else if (isUnread) {
          onMarkAsRead()
        }
      }}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          notification.type === 'access_request' ? 'bg-teal-100 text-teal-600' :
          notification.type === 'org_invite' ? 'bg-primary-100 text-primary-600' :
          requiresPayment ? 'bg-orange-100 text-orange-600' :
          notification.type === 'booking_approved' ? 'bg-green-100 text-green-600' :
          notification.type === 'booking_rejected' ? 'bg-red-100 text-red-600' :
          isBookingRequest ? 'bg-amber-100 text-amber-600' :
          'bg-gray-100 text-gray-600'
        }`}>
          {notification.type === 'access_request' ? (
            <UserPlus className="w-5 h-5" />
          ) : notification.type === 'org_invite' ? (
            <ChevronRight className="w-5 h-5" />
          ) : requiresPayment ? (
            <CreditCard className="w-5 h-5" />
          ) : isBookingNotification || isBookingRequest ? (
            <Calendar className="w-5 h-5" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
              {notification.title}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
          <p className="text-xs text-gray-400 mt-1">{formatTime(notification.createdAt)}</p>

          {/* Action buttons for invites */}
          {isInviteActionable && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onAccept()
                }}
                disabled={actionLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                Accept
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDecline()
                }}
                disabled={actionLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Decline
              </button>
            </div>
          )}

          {/* Action button for booking notifications */}
          {isBookingNotification && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onViewBooking()
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View Booking
              </button>
            </div>
          )}

          {/* Action button for booking request notifications (for admins to review) */}
          {isBookingRequest && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onViewBooking()
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Review Request
              </button>
            </div>
          )}

          {/* Action button for access request notifications */}
          {isAccessRequest && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onViewRequest()
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View Request
              </button>
            </div>
          )}

          {/* Actioned badge */}
          {notification.actionedAt && notification.type === 'org_invite' && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                <Check className="w-3 h-3" />
                Responded
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
